/**
 * Frota · Manutenção · Modelos de checklist.
 *
 * Lista os modelos (inclusive desativados) e os tipos de veículo/operação da
 * empresa. Criar e editar abrem o editor; desativar tira o modelo do início do
 * checklist sem apagar histórico. Quem não tem `template.manage` só consulta.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { MaintenanceTemplateSummaryDto, MaintenanceVehicleTypeDto } from "@bi/types";
import { api, ApiError } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { useCapabilities } from "../features/maintenance/useCapabilities";
import { KIND_LABEL } from "../features/maintenance/labels";
import { CARD_GRID, TableOrCards, useMobile } from "../features/maintenance-mobile/mobile-ui";

const IMPLEMENTO: Record<string, string> = { sider: "Sider", graneleiro: "Graneleiro", grade_baixa: "Grade Baixa" };

export function MaintenanceTemplatesPage() {
  const mobile = useMobile();
  const qc = useQueryClient();
  const { can } = useCapabilities();
  const gerencia = can("template.manage");
  const [erro, setErro] = useState<string | null>(null);
  const [novoTipo, setNovoTipo] = useState("");

  const modelos = useQuery({
    queryKey: ["maintenance", "templates", "all"],
    queryFn: () => api.get<{ items: MaintenanceTemplateSummaryDto[] }>("/maintenance/templates?all=1"),
  });
  const tipos = useQuery({
    queryKey: ["maintenance", "vehicle-types", "all"],
    queryFn: () => api.get<{ items: MaintenanceVehicleTypeDto[] }>("/maintenance/vehicle-types?all=1"),
  });

  const falha = (e: unknown) => setErro(e instanceof ApiError ? e.message : "Não foi possível salvar.");
  const ok = () => { setErro(null); qc.invalidateQueries({ queryKey: ["maintenance"] }); };

  const ativar = useMutation({
    mutationFn: (v: { id: string; active: boolean }) => api.patch(`/maintenance/templates/${v.id}`, { active: v.active }),
    onSuccess: ok, onError: falha,
  });
  const criarTipo = useMutation({
    mutationFn: () => api.post("/maintenance/vehicle-types", { name: novoTipo }),
    onSuccess: () => { setNovoTipo(""); ok(); }, onError: falha,
  });
  const editarTipo = useMutation({
    mutationFn: (v: { id: string; name?: string; active?: boolean }) => api.patch(`/maintenance/vehicle-types/${v.id}`, v),
    onSuccess: ok, onError: falha,
  });

  const lista = [...(modelos.data?.items ?? [])].sort(
    (a, b) => Number(b.active) - Number(a.active) || (a.vehicleType?.name ?? "").localeCompare(b.vehicleType?.name ?? "") || a.name.localeCompare(b.name),
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Frota · Manutenção · Modelos de checklist"
        subtitle="Questionários por tipo de veículo ou operação. Editar publica uma versão nova, e os checklists já feitos não mudam."
        actions={gerencia ? <Link className="btn-primary" to="/frota/manutencao/modelos/novo">Novo modelo</Link> : undefined}
      />

      {erro && <div className="card p-3 bg-danger-50 text-sm text-danger-600">{erro}</div>}

      <div className={mobile ? "" : "card overflow-x-auto"}>
        <TableOrCards
          cards={
            <div className={CARD_GRID}>
              {modelos.isLoading && <p className="card p-4 text-sm text-gray-500">Carregando…</p>}
              {lista.map((t) => (
                <div key={t.id} className={`card p-3.5 ${t.active ? "" : "opacity-60"}`}>
                  <div className="flex items-start justify-between gap-2">
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-gray-900">{t.name}</span>
                      <span className="block text-xs text-gray-500">
                        {KIND_LABEL[t.kind]} · {t.vehicleType?.name ?? (t.implementType ? `Cavalo + ${IMPLEMENTO[t.implementType]}` : "Qualquer veículo")}
                      </span>
                      <span className="block text-xs text-gray-500">
                        {t.itemCount} itens{t.publishedVersion ? ` · v${t.publishedVersion.version}` : " · rascunho"}
                      </span>
                    </span>
                    <span className={t.active ? "badge-green" : "badge-gray"}>{t.active ? "Ativo" : "Desativado"}</span>
                  </div>
                  {gerencia && (
                    <div className="mt-2.5 flex gap-2">
                      <Link className="btn-secondary min-h-[2.5rem] flex-1 justify-center" to={`/frota/manutencao/modelos/${t.id}`}>Editar</Link>
                      <button className="btn-ghost min-h-[2.5rem] flex-1 justify-center border border-surface-border" disabled={ativar.isPending} onClick={() => ativar.mutate({ id: t.id, active: !t.active })}>
                        {t.active ? "Desativar" : "Reativar"}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          }
          table={
        <table className="w-full text-sm">
          <thead className="bg-surface-muted text-xs text-gray-500">
            <tr>
              <th className="text-left px-4 py-2">Modelo</th>
              <th className="text-left px-4 py-2">Uso</th>
              <th className="text-left px-4 py-2">Veículo / operação</th>
              <th className="text-right px-4 py-2">Versão</th>
              <th className="text-right px-4 py-2">Itens</th>
              <th className="text-left px-4 py-2">Situação</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {modelos.isLoading && <tr><td className="px-4 py-6 text-gray-500" colSpan={7}>Carregando…</td></tr>}
            {lista.map((t) => (
              <tr key={t.id} className={t.active ? "" : "text-gray-400"}>
                <td className="px-4 py-2">
                  <div className="font-medium text-gray-900">{t.name}</div>
                  {t.description && <div className="text-xs text-gray-400 truncate max-w-md">{t.description}</div>}
                </td>
                <td className="px-4 py-2">{KIND_LABEL[t.kind]}</td>
                <td className="px-4 py-2">{t.vehicleType?.name ?? (t.implementType ? `Cavalo + ${IMPLEMENTO[t.implementType]}` : "Qualquer")}</td>
                <td className="px-4 py-2 text-right">{t.publishedVersion ? `v${t.publishedVersion.version}` : "—"}</td>
                <td className="px-4 py-2 text-right">{t.itemCount}</td>
                <td className="px-4 py-2"><span className={t.active ? "badge-green" : "badge-gray"}>{t.active ? "Ativo" : "Desativado"}</span></td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  {gerencia && (
                    <>
                      <Link className="btn-secondary text-xs" to={`/frota/manutencao/modelos/${t.id}`}>Editar</Link>
                      <button className="btn-ghost text-xs ml-1" disabled={ativar.isPending} onClick={() => ativar.mutate({ id: t.id, active: !t.active })}>
                        {t.active ? "Desativar" : "Reativar"}
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
          }
        />
      </div>


      <section className="card p-5 space-y-3">
        <div>
          <h3 className="font-semibold text-gray-900">Tipos de veículo ou operação</h3>
          <p className="text-sm text-gray-500">
            Organizam os modelos além do conjunto cavalo + carreta (que segue por Sider, Graneleiro e Grade Baixa). Ex.: Empilhadeira, Frota leve, Distribuição.
          </p>
        </div>
        <ul className="divide-y divide-gray-100">
          {(tipos.data?.items ?? []).map((t) => (
            <TipoLinha key={t.id} tipo={t} editavel={gerencia} onSalvar={(v) => editarTipo.mutate({ id: t.id, ...v })} />
          ))}
          {tipos.data?.items.length === 0 && <li className="py-2 text-sm text-gray-500">Nenhum tipo cadastrado.</li>}
        </ul>
        {gerencia && (
          <div className="flex gap-2">
            <input className="input min-w-0 flex-1 max-w-sm" placeholder="Novo tipo (ex.: Empilhadeira)" value={novoTipo} onChange={(e) => setNovoTipo(e.target.value)} />
            <button className="btn-secondary" disabled={novoTipo.trim().length < 2 || criarTipo.isPending} onClick={() => criarTipo.mutate()}>Adicionar</button>
          </div>
        )}
      </section>
    </div>
  );
}

function TipoLinha({ tipo, editavel, onSalvar }: {
  tipo: MaintenanceVehicleTypeDto; editavel: boolean; onSalvar: (v: { name?: string; active?: boolean }) => void;
}) {
  const [nome, setNome] = useState(tipo.name);
  return (
    <li className={`py-2 flex flex-wrap items-center gap-2 text-sm ${tipo.active ? "" : "text-gray-400"}`}>
      {editavel ? (
        <input className="input w-64" value={nome} onChange={(e) => setNome(e.target.value)} onBlur={() => nome.trim() !== tipo.name && nome.trim().length >= 2 && onSalvar({ name: nome })} />
      ) : (
        <span className="font-medium">{tipo.name}</span>
      )}
      <span className="text-xs text-gray-400">{tipo.templateCount ?? 0} modelo(s)</span>
      {!tipo.active && <span className="badge-gray">Desativado</span>}
      {editavel && (
        <button className="btn-ghost text-xs" onClick={() => onSalvar({ active: !tipo.active })}>{tipo.active ? "Desativar" : "Reativar"}</button>
      )}
    </li>
  );
}
